(() => {
  'use strict';

  const GLOBAL_KEY = 'RFNProductDiscovery';
  const ROOT_SELECTOR = '[data-rfn-product-discovery]';
  const EMBLA_SRC =
    'https://unpkg.com/embla-carousel@8.6.0/embla-carousel.umd.js';
  const MAX_VISIBLE_DOTS = 5;

  if (window[GLOBAL_KEY]?.initialized) {
    window[GLOBAL_KEY].refresh();
    return;
  }

  const instances = new WeakMap();

  const loadEmbla = () => {
    if (window.EmblaCarousel) {
      return Promise.resolve(window.EmblaCarousel);
    }

    if (window.__rfnEmblaLoading) {
      return window.__rfnEmblaLoading;
    }

    window.__rfnEmblaLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = EMBLA_SRC;
      script.async = true;
      script.onload = () => resolve(window.EmblaCarousel);
      script.onerror = () => reject(
        new Error('Unable to load Embla Carousel.')
      );
      document.head.appendChild(script);
    });

    return window.__rfnEmblaLoading;
  };

  const getBreakpoint = () => {
    if (window.matchMedia('(max-width: 699px)').matches) {
      return 'mobile';
    }

    if (window.matchMedia('(max-width: 1180px)').matches) {
      return 'tablet';
    }

    return 'desktop';
  };

  const getEmblaOptions = () => {
    const breakpoint = getBreakpoint();

    return {
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: false,
      skipSnaps: false,
      duration: breakpoint === 'desktop' ? 34 : 30,
      slidesToScroll:
        breakpoint === 'desktop'
          ? 4
          : breakpoint === 'tablet'
            ? 2
            : 1,
      watchDrag: true
    };
  };

  const sanitizeImageUrl = (value) => {
    if (!value) return '';

    let url = String(value).trim();

    if (url.includes('{width}')) {
      url = url.replace(/\{width\}/g, '720');
    }

    if (url.startsWith('//')) {
      url = `${window.location.protocol}${url}`;
    }

    return url;
  };

  const createRecentCardFromGrid = (grid) => {
    const productId = String(grid.dataset.productId || '');

    const sourceLink =
      grid.querySelector('a.grid-item__link') ||
      grid.querySelector('a[href]');

    if (!sourceLink) return null;

    const sourceImage =
      grid.querySelector('.grid-product__image-wrap img') ||
      grid.querySelector('img');

    const sourceTitle =
      grid.querySelector('.rfn-product-card__title') ||
      grid.querySelector('.grid-product__title');

    const sourceEditorial =
      grid.querySelector('.rfn-product-card__editorial') ||
      grid.querySelector('.grid-product__vendor');

    const sourcePrice =
      grid.querySelector('.grid-product__price');

    const imageUrl = sanitizeImageUrl(
      sourceImage?.dataset?.src ||
      sourceImage?.getAttribute('data-src') ||
      sourceImage?.currentSrc ||
      sourceImage?.getAttribute('src')
    );

    const article = document.createElement('article');
    article.className = 'rfn-discovery-card';
    article.dataset.rfnDiscoveryCard = '';
    article.dataset.productId = productId;

    const link = document.createElement('a');
    link.className = 'rfn-discovery-card__link';
    link.href = sourceLink.href;

    const media = document.createElement('div');
    media.className = 'rfn-discovery-card__media';

    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'rfn-discovery-card__image';
      image.src = imageUrl;
      image.alt =
        sourceImage?.alt ||
        sourceTitle?.textContent?.trim() ||
        '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      media.appendChild(image);
    }

    const meta = document.createElement('div');
    meta.className = 'rfn-discovery-card__meta';

    const title = document.createElement('h3');
    title.className = 'rfn-discovery-card__title';
    title.innerHTML =
      sourceTitle?.innerHTML ||
      sourceLink.getAttribute('aria-label') ||
      '';

    const editorial = document.createElement('div');
    editorial.className = 'rfn-discovery-card__editorial';
    editorial.textContent =
      sourceEditorial?.textContent?.trim() || '';

    const price = document.createElement('div');
    price.className = 'rfn-discovery-card__price';
    price.innerHTML = sourcePrice?.innerHTML || '';

    meta.append(title, editorial, price);
    link.append(media, meta);
    article.appendChild(link);

    return article;
  };

  const createInstance = (root) => {
    if (!root || instances.has(root)) {
      return instances.get(root);
    }

    const currentProductId = String(
      root.dataset.currentProductId || ''
    );

    const productsLimit = Number(
      root.dataset.productsLimit || 8
    );

    const recommendationsUrl =
      root.dataset.recommendationsUrl || '';

    const searchUrl =
      root.dataset.searchUrl || '/search';

    const tabs = [
      ...root.querySelectorAll('[data-rfn-discovery-tab]')
    ];

    const panels = [
      ...root.querySelectorAll('[data-rfn-discovery-panel]')
    ];

    const carousels = new Map();
    let activeType = 'recommendations';
    let resizeTimer = null;

    const getPanel = (type) => (
      root.querySelector(
        `[data-rfn-discovery-panel="${type}"]`
      )
    );

    const getTab = (type) => (
      root.querySelector(
        `[data-rfn-discovery-tab="${type}"]`
      )
    );

    const getCarouselState = (type) => {
      if (carousels.has(type)) {
        return carousels.get(type);
      }

      const panel = getPanel(type);
      if (!panel) return null;

      const state = {
        type,
        panel,
        carousel: panel.querySelector(
          '[data-rfn-discovery-carousel]'
        ),
        viewport: panel.querySelector(
          '[data-rfn-discovery-viewport]'
        ),
        track: panel.querySelector(
          '[data-rfn-discovery-track]'
        ),
        dots: panel.querySelector(
          '[data-rfn-discovery-dots]'
        ),
        previous: panel.querySelector(
          '[data-rfn-discovery-prev]'
        ),
        next: panel.querySelector(
          '[data-rfn-discovery-next]'
        ),
        empty: panel.querySelector(
          '[data-rfn-discovery-empty]'
        ),
        embla: null,
        bound: false,
        available: false
      };

      carousels.set(type, state);
      return state;
    };

    const getCards = (state) => (
      state
        ? [
            ...state.track.querySelectorAll(
              '[data-rfn-discovery-card]'
            )
          ]
        : []
    );

    const setPanelAvailability = (type, available) => {
      const state = getCarouselState(type);
      const tab = getTab(type);

      if (!state || !tab) return;

      state.available = Boolean(available);
      tab.hidden = !state.available;

      if (state.empty) {
        state.empty.hidden = state.available;
      }
    };

    const updateDotWindow = (state) => {
      if (!state?.embla) return;

      const dots = [
        ...state.dots.querySelectorAll(
          '[data-rfn-discovery-dot]'
        )
      ];

      const selected = state.embla.selectedScrollSnap();
      const count = dots.length;
      let start = 0;

      if (count > MAX_VISIBLE_DOTS) {
        start = Math.max(
          0,
          Math.min(
            selected - 2,
            count - MAX_VISIBLE_DOTS
          )
        );
      }

      const end = start + MAX_VISIBLE_DOTS;

      dots.forEach((dot, index) => {
        dot.classList.toggle(
          'is-window-hidden',
          count > MAX_VISIBLE_DOTS &&
            (index < start || index >= end)
        );
      });
    };

    const syncControls = (state) => {
      if (!state?.embla) return;

      const selected = state.embla.selectedScrollSnap();

      state.dots
        .querySelectorAll('[data-rfn-discovery-dot]')
        .forEach((dot, index) => {
          const active = index === selected;
          dot.classList.toggle('is-active', active);

          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

      updateDotWindow(state);
      state.previous.disabled = !state.embla.canScrollPrev();
      state.next.disabled = !state.embla.canScrollNext();
    };

    const renderDots = (state) => {
      state.dots.innerHTML = '';
      if (!state.embla) return;

      const snapCount = state.embla.scrollSnapList().length;

      for (let index = 0; index < snapCount; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'rfn-product-discovery__dot';
        dot.dataset.rfnDiscoveryDot = String(index);
        dot.setAttribute(
          'aria-label',
          `Go to product page ${index + 1}`
        );
        state.dots.appendChild(dot);
      }

      syncControls(state);
    };

    const bindCarousel = (state) => {
      if (state.bound) return;

      const setDragging = () => {
        state.track.classList.add('is-dragging');
      };

      const unsetDragging = () => {
        state.track.classList.remove('is-dragging');
      };

      state.previous.addEventListener('click', () => {
        state.embla?.scrollPrev();
      });

      state.next.addEventListener('click', () => {
        state.embla?.scrollNext();
      });

      state.dots.addEventListener('click', (event) => {
        const dot = event.target.closest(
          '[data-rfn-discovery-dot]'
        );

        if (!dot || !state.embla) return;

        state.embla.scrollTo(
          Number(dot.dataset.rfnDiscoveryDot || 0)
        );
      });

      state.track.addEventListener(
        'dragstart',
        (event) => event.preventDefault()
      );

      state.onPointerDown = setDragging;
      state.onPointerUp = unsetDragging;
      state.bound = true;
    };

    const initCarousel = async (
      type,
      { reset = false } = {}
    ) => {
      const state = getCarouselState(type);
      if (!state || !state.available) return;

      const cards = getCards(state);
      if (!cards.length) return;

      cards.forEach((card) => {
        card.querySelectorAll('img').forEach((image) => {
          image.draggable = false;
          image.setAttribute('draggable', 'false');
        });
      });

      await loadEmbla();

      const options = getEmblaOptions();

      if (state.embla) {
        state.embla.reInit(options);
      } else {
        state.embla = window.EmblaCarousel(
          state.viewport,
          options
        );

        bindCarousel(state);

        state.embla
          .on('select', () => syncControls(state))
          .on('reInit', () => {
            renderDots(state);
            syncControls(state);
          })
          .on('resize', () => syncControls(state))
          .on('pointerDown', state.onPointerDown)
          .on('pointerUp', state.onPointerUp)
          .on('settle', state.onPointerUp);
      }

      renderDots(state);

      if (reset) {
        state.embla.scrollTo(0, true);
      }

      syncControls(state);
    };

    const showTab = async (
      type,
      { focus = false } = {}
    ) => {
      const targetState = getCarouselState(type);

      if (!targetState?.available) {
        const fallbackType =
          getCarouselState('recommendations')?.available
            ? 'recommendations'
            : getCarouselState('recent')?.available
              ? 'recent'
              : null;

        if (!fallbackType) return;
        type = fallbackType;
      }

      activeType = type;

      tabs.forEach((tab) => {
        const active = tab.dataset.rfnDiscoveryTab === type;

        tab.classList.toggle('is-active', active);
        tab.setAttribute(
          'aria-selected',
          active ? 'true' : 'false'
        );

        if (active && focus) {
          tab.focus({ preventScroll: true });
        }
      });

      panels.forEach((panel) => {
        const active = panel.dataset.rfnDiscoveryPanel === type;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      /*
        Do not call scrollIntoView here.
        showTab() also runs automatically during initial page load;
        scrolling the active tab into view would pull the entire product
        page down to the Discovery / Review area.
      */
      window.requestAnimationFrame(() => {
        initCarousel(type, { reset: true }).catch(() => {
          root.classList.add('is-carousel-fallback');
        });
      });
    };

    const parseRecommendations = async () => {
      const state = getCarouselState('recommendations');

      if (!state || !recommendationsUrl) {
        setPanelAvailability('recommendations', false);
        return;
      }

      try {
        const response = await fetch(recommendationsUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });

        if (!response.ok) {
          throw new Error(
            `Recommendations returned ${response.status}`
          );
        }

        const html = await response.text();
        const documentObject =
          new DOMParser().parseFromString(html, 'text/html');

        const source = documentObject.querySelector(
          '[data-rfn-discovery-source="recommendations"]'
        );

        const cards = source
          ? [
              ...source.querySelectorAll(
                '[data-rfn-discovery-card]'
              )
            ]
          : [];

        state.track.innerHTML = '';
        const seen = new Set();

        cards.forEach((card) => {
          const id = String(card.dataset.productId || '');

          if (
            !id ||
            id === currentProductId ||
            seen.has(id)
          ) {
            return;
          }

          seen.add(id);
          state.track.appendChild(card.cloneNode(true));
        });

        setPanelAvailability(
          'recommendations',
          getCards(state).length > 0
        );
      } catch (error) {
        console.warn(
          '[RFN Product Discovery] Recommendations:',
          error
        );
        setPanelAvailability('recommendations', false);
      }
    };

    const readRecentIds = () => {
      try {
        const raw = window.localStorage.getItem('recently-viewed');
        const parsed = raw ? JSON.parse(raw) : [];

        if (!Array.isArray(parsed)) return [];

        const seen = new Set();

        return parsed
          .map(String)
          .filter((id) => {
            if (
              !id ||
              id === currentProductId ||
              seen.has(id)
            ) {
              return false;
            }

            seen.add(id);
            return true;
          })
          .slice(0, productsLimit);
      } catch (error) {
        return [];
      }
    };

    const parseRecentProducts = async () => {
      const state = getCarouselState('recent');
      if (!state) return;

      const ids = readRecentIds();

      if (!ids.length) {
        setPanelAvailability('recent', false);
        return;
      }

      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = new URL(searchUrl, window.location.origin);

      url.searchParams.set('view', 'recently-viewed');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', query);

      try {
        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });

        if (!response.ok) {
          throw new Error(
            `Recently viewed returned ${response.status}`
          );
        }

        const html = await response.text();
        const documentObject =
          new DOMParser().parseFromString(html, 'text/html');

        const grids = [
          ...documentObject.querySelectorAll(
            '.grid-product[data-product-id]'
          )
        ];

        const byId = new Map();

        grids.forEach((grid) => {
          const id = String(grid.dataset.productId || '');
          if (id && !byId.has(id)) {
            byId.set(id, grid);
          }
        });

        state.track.innerHTML = '';

        ids.forEach((id) => {
          const grid = byId.get(id);
          if (!grid) return;

          const card = createRecentCardFromGrid(grid);
          if (card) state.track.appendChild(card);
        });

        setPanelAvailability(
          'recent',
          getCards(state).length > 0
        );
      } catch (error) {
        console.warn(
          '[RFN Product Discovery] Recently viewed:',
          error
        );
        setPanelAvailability('recent', false);
      }
    };

    const finalize = async () => {
      const recommendationsAvailable =
        getCarouselState('recommendations')?.available;

      const recentAvailable =
        getCarouselState('recent')?.available;

      if (!recommendationsAvailable && !recentAvailable) {
        root.hidden = true;
        return;
      }

      root.hidden = false;

      const preferred = recommendationsAvailable
        ? 'recommendations'
        : 'recent';

      await showTab(preferred);
    };

    const load = async () => {
      await Promise.all([
        parseRecommendations(),
        parseRecentProducts()
      ]);

      await finalize();
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        showTab(tab.dataset.rfnDiscoveryTab);
      });

      tab.addEventListener('keydown', (event) => {
        if (
          event.key !== 'ArrowLeft' &&
          event.key !== 'ArrowRight'
        ) {
          return;
        }

        const availableTabs = tabs.filter((item) => !item.hidden);
        const currentIndex = availableTabs.indexOf(tab);
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex =
          (
            currentIndex +
            direction +
            availableTabs.length
          ) % availableTabs.length;

        event.preventDefault();

        showTab(
          availableTabs[nextIndex].dataset.rfnDiscoveryTab,
          { focus: true }
        );
      });
    });

    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(resizeTimer);

        resizeTimer = window.setTimeout(() => {
          carousels.forEach((state) => {
            if (!state.embla) return;

            state.embla.reInit(getEmblaOptions());
            renderDots(state);
            syncControls(state);
          });
        }, 120);
      },
      { passive: true }
    );

    const instance = {
      refresh: () => {
        const state = getCarouselState(activeType);

        if (state?.embla) {
          state.embla.reInit(getEmblaOptions());
          renderDots(state);
          syncControls(state);
        }
      },

      reload: load,

      destroy: () => {
        carousels.forEach((state) => {
          state.embla?.destroy();
        });

        instances.delete(root);
      }
    };

    instances.set(root, instance);
    load();

    return instance;
  };

  const init = (scope = document) => {
    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(createInstance);
  };

  const refresh = () => {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => {
        const instance =
          instances.get(root) || createInstance(root);
        instance?.refresh();
      });
  };

  window[GLOBAL_KEY] = {
    initialized: true,
    init,
    refresh
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(),
      { once: true }
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => init(event.target)
  );
})();
